'use client';

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Landmark, Pencil, Plus, Star, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';
import { usePartnerAdmin } from '@/hooks/use-partner-admin';
import { trpc } from '@/lib/api/trpc';

import {
  emptyInstrument,
  InstrumentEditorDialog,
  type EditableInstrument,
} from './instrument-editor';

const STATUS_STYLE: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  inactive: 'bg-muted text-muted-foreground',
  pending_details: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
};

export default function PartnerAdminPayoutsPage() {
  const { t } = useTranslation('partnerPayouts');
  const { toast } = useToast();
  const { partner: partnerParam } = useParams<{ partner: string }>();
  const { partner, canView, isSuperAdmin } = usePartnerAdmin(partnerParam);
  const utils = trpc.useUtils();

  const [editor, setEditor] = useState<EditableInstrument | null>(null);

  const { data, isLoading, error } = trpc.partnerPaymentInstruments.list.useQuery(
    { partner: partner ?? undefined },
    { enabled: canView && !!partner, refetchOnWindowFocus: false },
  );

  const canEdit = isSuperAdmin && !!data?.canEdit;

  const setDefaultMutation = trpc.partnerPaymentInstruments.setDefault.useMutation({
    onSuccess: () => {
      utils.partnerPaymentInstruments.list.invalidate();
      toast({ title: t('opsDefaultSet') });
    },
    onError: (e) => toast({ title: t('opsSaveFailed'), description: e.message, variant: 'destructive' }),
  });
  const archiveMutation = trpc.partnerPaymentInstruments.archive.useMutation({
    onSuccess: () => {
      utils.partnerPaymentInstruments.list.invalidate();
      toast({ title: t('opsArchived') });
    },
    onError: (e) => toast({ title: t('opsSaveFailed'), description: e.message, variant: 'destructive' }),
  });

  if (!canView || !partner) return null;

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="py-8 text-center">
          <p className="text-destructive font-medium">{t('loadError')}</p>
          <p className="text-muted-foreground mt-1 text-sm">{error.message}</p>
        </CardContent>
      </Card>
    );
  }

  const instruments = data?.instruments ?? [];
  const showIban = instruments.some((i) => i.bankDetails.ibanLast4);
  const showRouting = instruments.some((i) => i.bankDetails.routingNumber);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{t('title')}</h2>
          <p className="text-muted-foreground text-sm">
            {canEdit ? t('opsDescription') : t('description')}
          </p>
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => setEditor(emptyInstrument())}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            {t('opsAdd')}
          </Button>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : instruments.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Landmark className="h-4 w-4" />
              {t('emptyTitle')}
            </CardTitle>
            <CardDescription>{t('emptyBody')}</CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('colLabel')}</TableHead>
                    <TableHead>{t('colBeneficiary')}</TableHead>
                    <TableHead>{t('colBank')}</TableHead>
                    <TableHead>{t('colAccount')}</TableHead>
                    {showRouting && <TableHead>{t('colRouting')}</TableHead>}
                    {showIban && <TableHead>{t('colIban')}</TableHead>}
                    <TableHead>{t('colMethod')}</TableHead>
                    <TableHead>{t('colCurrency')}</TableHead>
                    <TableHead>{t('colStatus')}</TableHead>
                    {canEdit && <TableHead className="text-right">{t('opsActions')}</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {instruments.map((i) => (
                    <TableRow key={i.id}>
                      <TableCell className="font-medium">
                        <div className="flex flex-wrap items-center gap-2">
                          <span>{i.label}</span>
                          {i.isDefault && (
                            <Badge variant="secondary" className="text-xs">
                              {t('default')}
                            </Badge>
                          )}
                        </div>
                        {i.mercuryNickname && (
                          <p className="text-muted-foreground mt-0.5 text-xs">{i.mercuryNickname}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {i.bankDetails.beneficiaryName || i.mercuryRecipientName || '—'}
                      </TableCell>
                      <TableCell className="text-sm">{i.bankDetails.bankName || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {i.bankDetails.accountNumberLast4
                          ? t('accountEnding', { last4: i.bankDetails.accountNumberLast4 })
                          : '—'}
                      </TableCell>
                      {showRouting && (
                        <TableCell className="font-mono text-xs">
                          {i.bankDetails.routingNumber || '—'}
                        </TableCell>
                      )}
                      {showIban && (
                        <TableCell className="font-mono text-xs">
                          {i.bankDetails.ibanLast4
                            ? t('ibanEnding', { last4: i.bankDetails.ibanLast4 })
                            : '—'}
                        </TableCell>
                      )}
                      <TableCell className="text-sm">
                        {t(`method.${i.method}`, { defaultValue: i.method })}
                      </TableCell>
                      <TableCell className="text-sm">{i.currency}</TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            STATUS_STYLE[i.status] ?? 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {t(`status.${i.status}`, { defaultValue: i.status })}
                        </span>
                      </TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {!i.isDefault && (
                              <Button
                                variant="ghost"
                                size="icon"
                                title={t('opsSetDefault')}
                                disabled={setDefaultMutation.isPending}
                                onClick={() => setDefaultMutation.mutate({ partner, id: i.id })}
                              >
                                <Star className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              title={t('opsEdit')}
                              onClick={() => {
                                const ops = i as typeof i & {
                                  mercuryRecipientId?: string | null;
                                  notes?: string | null;
                                };
                                setEditor({
                                  id: i.id,
                                  label: i.label,
                                  status: i.status as EditableInstrument['status'],
                                  isDefault: i.isDefault,
                                  method: i.method as EditableInstrument['method'],
                                  currency: i.currency,
                                  mercuryRecipientId: ops.mercuryRecipientId ?? '',
                                  mercuryRecipientName: i.mercuryRecipientName ?? '',
                                  mercuryNickname: i.mercuryNickname ?? '',
                                  notes: ops.notes ?? '',
                                  bankDetails: { ...i.bankDetails },
                                });
                              }}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title={t('opsArchive')}
                              disabled={archiveMutation.isPending}
                              onClick={() => {
                                if (!confirm(t('opsArchiveConfirm', { label: i.label }))) return;
                                archiveMutation.mutate({ partner, id: i.id });
                              }}
                            >
                              <Trash2 className="text-destructive h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {!canEdit && (
              <p className="text-muted-foreground mt-4 text-xs">{t('contactHint')}</p>
            )}
          </CardContent>
        </Card>
      )}

      {canEdit && editor && (
        <InstrumentEditorDialog
          key={editor.id ?? 'new'}
          partner={partner}
          open
          initial={editor}
          onClose={() => setEditor(null)}
          onSaved={() => utils.partnerPaymentInstruments.list.invalidate()}
        />
      )}
    </div>
  );
}
